import { Button, Checkbox, Code, FileInput, Group, Select, Stack, Table, Text } from "@mantine/core";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Content } from "shared/components/global/Content";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { runAsync } from "shared/utils/fns";
import { maybe } from "shared/utils/maybe";
import { notify } from "shared/utils/notify";
import { graphql } from "src/gql";
import { Language } from "src/gql/graphql";
import { Cm, ImportUserInput, ImportUserInputSchema } from "src/gql/zod";

const Page = () => {
    const [users, setUsers] = useState<ImportUserInput[]>();

    const importUsers = useGraphqlMutation(
        graphql(`
            mutation ImportUsersSupportTool($data: ImportUsersParametersInput!) {
                importUsers(data: $data) {
                    userCount
                    emailEudCount
                    smsEudCount
                }
            }
        `),
    );

    const form = useForm({
        initialValues: { autoVerifyEuds: false, importAsOptInUser: false, language: Language.English },
        mode: "uncontrolled",
    });

    const { autoVerifyEuds, importAsOptInUser, language } = form.getValues();

    const emailCount = users?.reduce((prev, user) => prev + user.euds.filter((eud) => eud.type == Cm.Email).length, 0);
    const smsCount = users?.reduce((prev, user) => prev + user.euds.filter((eud) => eud.type == Cm.Sms).length, 0);

    return (
        <Content paper m="xl" bd="none">
            <Stack align="center" maw="100%" m="xl">
                <FileInput
                    accept="application/json"
                    placeholder="Select JSON file"
                    onChange={(file) => {
                        const r = new FileReader();
                        r.onload = (e) => {
                            if (!e?.target?.result || typeof e.target.result != "string") throw Error("Error reading file");
                            const maybeUsers: ImportUserInput[] = JSON.parse(e.target!.result as string).map((it: unknown) =>
                                ImportUserInputSchema().parse(it),
                            );
                            setUsers(maybeUsers);
                        };
                        if (file) r.readAsText(file);
                    }}
                    clearable
                />
                <Group>
                    <Checkbox label="Auto verify EUDs?" {...form.getInputProps("autoVerifyEuds", { type: "checkbox" })} />
                    <Checkbox label="Import as Opt-In?" {...form.getInputProps("importAsOptInUser", { type: "checkbox" })} />
                    <Select data={Object.values(Language)} {...form.getInputProps("language")} />
                </Group>
                {!!users ? (
                    <Stack maw="85%" align="center">
                        <Group>
                            <Text>Users: {users.length}</Text>
                            <Text>SMS #s: {smsCount}</Text>
                            <Text>Emails: {emailCount}</Text>
                        </Group>
                        <Table.ScrollContainer minWidth={600} mah={400} style={{ overflow: "auto" }}>
                            <Table>
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th>Name</Table.Th>
                                        <Table.Th>Address</Table.Th>
                                        <Table.Th>EUDs</Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {users.map((user) => (
                                        <Table.Tr key={user.name}>
                                            <Table.Td>{user.name ?? ""}</Table.Td>
                                            <Table.Td>
                                                {maybe(user.address)?.take(
                                                    (address) =>
                                                        `${address.street} ${address.street2} ${address.city}, ${address.state} ${address.zip}`,
                                                )}
                                            </Table.Td>
                                            <Table.Td maw={400} style={{ overflowX: "auto", whiteSpace: "nowrap", textOverflow: "clip" }}>
                                                {user.euds.map((eud) => eud.value).join(", ")}
                                            </Table.Td>
                                        </Table.Tr>
                                    ))}
                                </Table.Tbody>
                            </Table>
                        </Table.ScrollContainer>
                        <Button
                            loading={importUsers.isPending}
                            onClick={() => {
                                runAsync(async () => {
                                    const { close } = await notify.confirm(
                                        <Stack>
                                            <Text>Are you sure you want to import?</Text>
                                            <Code>
                                                <Text>Users: {users.length}</Text>
                                                <Text>SMS #s: {smsCount}</Text>
                                                <Text>Emails: {emailCount}</Text>
                                                <Text>
                                                    Auto Verify EUDs:{" "}
                                                    {autoVerifyEuds ? (
                                                        <span style={{ color: "green" }}>TRUE</span>
                                                    ) : (
                                                        <span style={{ color: "red" }}>FALSE</span>
                                                    )}
                                                </Text>
                                                <Text>
                                                    Opt-In Mode:{" "}
                                                    {importAsOptInUser ? (
                                                        <span style={{ color: "green" }}>TRUE</span>
                                                    ) : (
                                                        <span style={{ color: "red" }}>FALSE</span>
                                                    )}
                                                </Text>
                                                <Text>Language: {language}</Text>
                                            </Code>
                                        </Stack>,
                                    );

                                    close();

                                    importUsers
                                        .mutateAsync({
                                            data: {
                                                users,
                                                language,
                                                importAsOptInUser,
                                                autoVerifyEuds,
                                            },
                                        })
                                        .then(({ importUsers }) =>
                                            modals.open({
                                                children: (
                                                    <Stack>
                                                        <Code>
                                                            <Text>Users: {importUsers.userCount}</Text>
                                                            <Text>SMS #s: {importUsers.smsEudCount}</Text>
                                                            <Text>Emails: {importUsers.emailEudCount}</Text>
                                                        </Code>
                                                    </Stack>
                                                ),
                                            }),
                                        )
                                        .catch(notify.catch);
                                });
                            }}
                        >
                            Submit
                        </Button>
                    </Stack>
                ) : (
                    <>
                        <Text>Expects JSON file with the format:</Text>
                        <Code block>{`Array<{
    // Will be added as an EUD so don't specify in users.euds as well
    // MUST specify for full users, MUST NOT specify for opt-in users
    loginEmail: String | undefined 
    euds: { value: String, type: CM }[]
    
    // Only for full users (can be empty list)
    // List groups as names, must be exact match
    groups: String[]
    
    name: String | undefined
    address: {
        name: String,
        street: String,
        street2: String | undefined
        city: String
        state: String
        zip: String
    } | undefined
}>
`}</Code>
                        <Text>Example:</Text>
                        <Code block>{`[
  {
    "name": "Dylan ImportTest1",
    "address": {
      "name": "Work",
      "street": "59 Halstead St",
      "city": "Rochester",
      "state": "NY",
      "zip": "14610"
    },
    "euds": [
      {
        "type": "SMS",
        "value": "(222)333-4444"
      }
    ],
    "groups": []
  },
  {
    "name": "Dylan ImportTest2",
    "address": {
      "name": "",
      "street": "59 Halstead St",
      "street2": "Apt 1",
      "city": "Rochester",
      "state": "NY",
      "zip": "14610"
    },
    "euds": [
      {
        "type": "EMAIL",
        "value": "dylan+importtest2@bryx.com"
      }
    ],
    "groups": []
}]
`}</Code>
                    </>
                )}
            </Stack>
        </Content>
    );
};

/** @public */
export const Route = createFileRoute("/_auth/_dev/devtools/user-import")({
    component: Page,
});
